import React, { useEffect, useMemo, useState } from "react";
import {
  collection,
  getDocs,
  deleteDoc,
  doc,
  addDoc,
  query,
  orderBy,
  serverTimestamp,
  Timestamp,
  updateDoc,
  where,
} from "firebase/firestore";
import { db } from "../../firebase";
import { useSwipeable } from "react-swipeable";
import {
  TrashIcon,
  ArrowPathIcon,
  PaperAirplaneIcon,
} from "@heroicons/react/24/solid";
import { getAuth } from "firebase/auth";
import { sendNotification } from "../../utils/sendNotification";
import { buildFunctionUrl, FUNCTIONS_BASE_URL } from "../../config/functions";

const API_BASE = FUNCTIONS_BASE_URL;

async function updateLead(leadId, data) {
  const res = await fetch(`${API_BASE}/updateLeadStatus`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ leadId, ...data }),
  });
  if (!res.ok) throw new Error(await res.text());
}

const buildCampaignTemplates = (lead = {}) => {
  const interestList = (lead.cardInformation || []).map((item) =>
    (item || "").toLowerCase()
  );
  const source = (lead.source || "").toLowerCase();
  const templates = [];
  const firstName = lead.name?.split(" ")[0] || "Hola";
  const serviceHint = interestList.find((item) => item);

  templates.push({
    id: "prwd-demo",
    label: "Cotización ventanas y puertas",
    channel: "email",
    subject: "Tu propuesta de Puerto Rico Windows & Doors está lista",
    message: `${firstName}, te escribe Puerto Rico Windows & Doors. Ya tenemos tu estimado para ${serviceHint || 'ventanas de impacto'}. Revisa ejemplos en https://puertoricowindows.com y agenda tu llamada al 787-236-9189.`,
    html: `<p>Hola ${firstName},</p><p>Te saluda <strong>Puerto Rico Windows & Doors</strong>. Ya preparamos tu cotización para ${serviceHint || 'nuestros sistemas de impacto'}.</p><p>Conoce nuestros casos en <a href="https://puertoricowindows.com" target="_blank">puertoricowindows.com</a> y responde este correo o llámanos al <strong>787-236-9189</strong> para coordinar instalación.</p><p>Equipo Puerto Rico Windows & Doors</p>`,
  });

  if ((lead.aiScore || 0) >= 80) {
    templates.push({
      id: "hot-lead-reminder",
      label: "Seguimiento prioritario",
      channel: "sms",
      message: `${firstName}, te habla Puerto Rico Windows & Doors. Tenemos listo tu estimado${serviceHint ? ` para ${serviceHint}` : ''}. Escríbenos a info@puertoricowindows.com o contesta este mensaje para reservar instalación.`,
    });
  }

  if (source.includes("tiktok") || source.includes("instagram")) {
    templates.push({
      id: "social-proof",
      label: "Campaña redes sociales",
      channel: "email",
      subject: "Calidad garantizada para tu proyecto",
      message: `${firstName}, somos Puerto Rico Windows & Doors. Apartamos un espacio para mostrarte nuestras ventanas y puertas certificadas. Ejemplos: https://puertoricowindows.com. Escríbenos a info@puertoricowindows.com o 787-236-9189.`,
      html: `<p>Hola ${firstName},</p><p>Te escribe el equipo de <strong>Puerto Rico Windows & Doors</strong>. Queremos invitarte a conocer nuestros proyectos de ventanas y puertas con garantía total.</p><p>Mira casos reales aquí: <a href="https://puertoricowindows.com" target="_blank">Puerto Rico Windows & Doors</a>.</p><p>Contáctanos en <a href="mailto:info@puertoricowindows.com">info@puertoricowindows.com</a> o al <strong>787-236-9189</strong>.</p>`,
    });
  }

  if (templates.length === 0) {
    templates.push({
      id: "general-reminder",
      label: "Recordatorio general",
      channel: "whatsapp",
      message: `${firstName}, te habla Puerto Rico Windows & Doors. Instalamos ventanas y puertas de impacto con servicio rápido. Escríbenos a info@puertoricowindows.com o WhatsApp 787-236-9189.`,
    });
    templates.push({
      id: "advantages-email",
      label: "Ventajas del servicio (email)",
      channel: "email",
      subject: "Por qué elegir Puerto Rico Windows & Doors",
      message: `${firstName}, somos Puerto Rico Windows & Doors. Entregamos cotizaciones inmediatas, instalación garantizada y soporte postventa. Agenda en info@puertoricowindows.com o 787-236-9189.`,
      html: `<p>Hola ${firstName},</p>
        <p>Te comparte el equipo de <strong>Puerto Rico Windows & Doors</strong> algunas ventajas de trabajar con nosotros:</p>
        <ul>
          <li>Mediciones y cotización inteligente en minutos.</li>
          <li>Instalación rápida con personal certificado.</li>
          <li>Garantía de materiales y servicio postventa.</li>
        </ul>
        <p>¿Conversamos? Escríbenos a <a href="mailto:info@puertoricowindows.com">info@puertoricowindows.com</a> o llama/WhatsApp al <strong>787-236-9189</strong>.</p>
        <p>Equipo Puerto Rico Windows & Doors</p>`,
    });
  }

  return templates;
};

async function convertLead(leadId) {
  const res = await fetch(`${API_BASE}/convertLeadToClient`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ leadId }),
  });
  if (!res.ok) throw new Error(await res.text());
  return res.json();
}

//  Badge helpers
const statusBadge = (s = "") => {
  const v = s.toLowerCase();
  if (v === "convertido") return "bg-green-100 text-green-800";
  if (v === "en_proceso") return "bg-blue-100 text-blue-800";
  if (v === "inactivo") return "bg-gray-100 text-gray-800";
  return "bg-yellow-100 text-yellow-800";
};

const sourceBadge = (s = "") => "bg-orange-100 text-orange-800";
const LeadsList = () => {
  const [leads, setLeads] = useState([]);
  const [notification, setNotification] = useState("");
  const [statusFilter, setStatusFilter] = useState("activos");
  const [assignmentFilter, setAssignmentFilter] = useState("all");
  const [leadToDelete, setLeadToDelete] = useState(null);
  const [detailLead, setDetailLead] = useState(null);
  const [timelineLead, setTimelineLead] = useState(null);
  const [timelineEntries, setTimelineEntries] = useState([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [noteForm, setNoteForm] = useState({
    summary: "",
    channel: "whatsapp",
    schedule: "",
    nextStep: "",
  });
  const [sendingCampaign, setSendingCampaign] = useState(false);
  const [sellers, setSellers] = useState([]);
  const [loadingSellers, setLoadingSellers] = useState(false);
  const [assigningLeadId, setAssigningLeadId] = useState("");
  const auth = getAuth();

  const fetchLeads = async () => {
    try {
      const querySnapshot = await getDocs(collection(db, "leads"));
      const leadsData = querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      setLeads(leadsData);
    } catch (error) {
      console.error("Error al obtener leads:", error);
    }
  };

  const openTimelineModal = async (lead) => {
    setTimelineLead(lead);
    setTimelineLoading(true);
    try {
      const timelineRef = collection(db, "leads", lead.id, "timeline");
      const timelineSnap = await getDocs(query(timelineRef, orderBy("createdAt", "desc")));
      const entries = timelineSnap.docs.map((docSnap) => ({
        id: docSnap.id,
        ...docSnap.data(),
      }));
      setTimelineEntries(entries);
    } catch (error) {
      console.error("Error al cargar seguimiento:", error);
    } finally {
      setTimelineLoading(false);
    }
  };

  const closeTimelineModal = () => {
    setTimelineLead(null);
    setTimelineEntries([]);
    setNoteForm({
      summary: "",
      channel: "whatsapp",
      schedule: "",
      nextStep: "",
    });
    setSendingCampaign(false);
  };

  const handleTimelineSubmit = async (e) => {
    e.preventDefault();
    if (!timelineLead || !noteForm.summary.trim()) return;

    try {
      const timelineRef = collection(db, "leads", timelineLead.id, "timeline");
      const remindDate = noteForm.schedule ? Timestamp.fromDate(new Date(noteForm.schedule)) : null;
      const payload = {
        summary: noteForm.summary.trim(),
        channel: noteForm.channel,
        nextStep: noteForm.nextStep.trim() || null,
        remindAt: remindDate,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "admin",
      };

      await addDoc(timelineRef, payload);

      await updateDoc(doc(db, "leads", timelineLead.id), {
        lastContactAt: serverTimestamp(),
        lastContactNote: payload.summary,
        nextFollowUpAt: remindDate,
        nextStep: payload.nextStep,
      }).catch(() => {});

      if (noteForm.schedule) {
        try {
          const isoSchedule = new Date(noteForm.schedule).toISOString();
          await fetch(`${API_BASE}/scheduleLeadReminder`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              schedule: isoSchedule,
              summary: payload.summary,
              channel: noteForm.channel,
              lead: {
                id: timelineLead.id,
                name: timelineLead.name,
                email: timelineLead.email,
                phone: timelineLead.phone,
              },
            }),
          });
        } catch (err) {
          console.error("No se pudo agendar recordatorio en Calendar:", err);
        }
      }

      setNoteForm({
        summary: "",
        channel: noteForm.channel,
        schedule: "",
        nextStep: "",
      });
      openTimelineModal(timelineLead);
      setNotification(`Seguimiento guardado para ${timelineLead.name || timelineLead.email}`);
      setTimeout(() => setNotification(""), 3000);
    } catch (error) {
      console.error("Error guardando seguimiento:", error);
    }
  };

  const handleSendCampaign = async (template) => {
    if (!timelineLead) return;
    setSendingCampaign(true);
    try {
      if (template.channel === "email") {
        await sendNotification("email", {
          to: timelineLead.email,
          subject: template.subject || "Seguimiento Puerto Rico Windows & Doors",
          html:
            template.html ||
            `<p>${template.message}</p><p>Equipo Puerto Rico Windows & Doors</p>`,
        });
      } else if (template.channel === "sms" || template.channel === "whatsapp") {
        const phone = timelineLead.phone?.trim();
        if (!phone) throw new Error("El lead no tiene teléfono");
        const formattedPhone = phone.startsWith("+") ? phone : `+1${phone}`;
        await sendNotification("sms", {
          to: formattedPhone,
          message: template.message,
        });
      }

      const timelineRef = collection(db, "leads", timelineLead.id, "timeline");
      await addDoc(timelineRef, {
        summary: `Campaña "${template.label}" enviada`,
        channel: template.channel,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "admin",
        type: "campaign",
      });
      openTimelineModal(timelineLead);
      setNotification(`Campaña "${template.label}" enviada a ${timelineLead.name || timelineLead.email}`);
      setTimeout(() => setNotification(""), 3000);
    } catch (error) {
      console.error("Error enviando campaña:", error);
      setNotification(`No se pudo enviar la campaña: ${template.label}`);
      setTimeout(() => setNotification(""), 3000);
    } finally {
      setSendingCampaign(false);
    }
  };

  const handleDelete = (lead) => {
    setLeadToDelete(lead);
  };

  const handleAssignSeller = async (leadId, sellerId) => {
    if (!sellerId) return;
    const seller =
      sellers.find((member) => member.id === sellerId) ||
      sellers.find((member) => member.uid === sellerId);
    if (!seller) return;
    setAssigningLeadId(leadId);
    try {
      const leadRef = doc(db, "leads", leadId);
      const sellerIdentifier = seller.uid || seller.id;
      await updateDoc(leadRef, {
        assignedTo: sellerIdentifier,
        assignedToUid: seller.uid || sellerIdentifier,
        assignedToEmail: seller.email,
        assignedToName: seller.name || seller.email,
        assignedToPhone: seller.phone || null,
        assignedAt: serverTimestamp(),
      });
      await addDoc(collection(db, "leads", leadId, "timeline"), {
        summary: `Lead asignado a ${seller.name || seller.email}`,
        channel: "sistema",
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "admin",
        type: "assignment",
      });
      setNotification(`Lead asignado a ${seller.name || seller.email}`);
      fetchLeads();
      setTimeout(() => setNotification(""), 3000);
    } catch (error) {
      console.error("Error asignando vendedor:", error);
      setNotification("No se pudo asignar el vendedor.");
      setTimeout(() => setNotification(""), 3000);
    } finally {
      setAssigningLeadId("");
    }
  };

  const confirmDelete = async () => {
    if (!leadToDelete) return;
    try {
      await deleteDoc(doc(db, "leads", leadToDelete.id));
      setNotification(`Lead "${leadToDelete.name}" eliminado`);
      setLeadToDelete(null);
      fetchLeads();
      setTimeout(() => setNotification(""), 3000);
    } catch (error) {
      console.error("Error eliminando lead:", error);
    }
  };

  const cancelDelete = () => {
    setLeadToDelete(null);
  };

  const openDetailModal = (lead) => setDetailLead(lead);
  const closeDetailModal = () => setDetailLead(null);

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    const loadSellers = async () => {
      setLoadingSellers(true);
      try {
        const q = query(
          collection(db, "teamMembers"),
          where("role", "==", "seller")
        );
        const snap = await getDocs(q);
        const sellerData = snap.docs.map((docSnap) => ({
          id: docSnap.id,
          ...docSnap.data(),
        }));
        setSellers(sellerData);
      } catch (error) {
        console.error("Error cargando vendedores:", error);
      } finally {
        setLoadingSellers(false);
      }
    };
    loadSellers();
  }, []);

  const normalizedStatus = (leadStatus = "") => (leadStatus || "").toLowerCase();
  const isLeadAssigned = (lead = {}) =>
    Boolean(lead.assignedToEmail || lead.assignedToUid || lead.assignedTo);

  const statusCounts = useMemo(() => {
    return leads.reduce(
      (acc, lead) => {
        const status = normalizedStatus(lead.status || "nuevo");
        if (status === "convertido") acc.convertidos += 1;
        else if (status === "inactivo") acc.inactivos += 1;
        else acc.activos += 1;
        return acc;
      },
      { activos: 0, inactivos: 0, convertidos: 0 }
    );
  }, [leads]);

  const assignmentCounts = useMemo(() => {
    return leads.reduce(
      (acc, lead) => {
        if (isLeadAssigned(lead)) acc.assigned += 1;
        else acc.unassigned += 1;
        return acc;
      },
      { assigned: 0, unassigned: 0 }
    );
  }, [leads]);

  const filteredLeads = useMemo(() => {
    let subset = leads;
    if (assignmentFilter === "unassigned") {
      subset = subset.filter((lead) => !isLeadAssigned(lead));
    } else if (assignmentFilter === "assigned") {
      subset = subset.filter((lead) => isLeadAssigned(lead));
    }

    if (statusFilter === "todos") return subset;
    return subset.filter((lead) => {
      const status = normalizedStatus(lead.status || "nuevo");
      if (statusFilter === "activos") {
        return status !== "convertido" && status !== "inactivo";
      }
      if (statusFilter === "inactivos") {
        return status === "inactivo";
      }
      if (statusFilter === "convertidos") {
        return status === "convertido";
      }
      return true;
    });
  }, [leads, statusFilter, assignmentFilter]);

  const campaignTemplates = timelineLead ? buildCampaignTemplates(timelineLead) : [];

  const LeadCard = ({ lead }) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const swipeHandlers = useSwipeable({
      onSwipedLeft: () => {
        setIsDeleting(true);
        setTimeout(() => handleDelete(lead), 300);
      },
      preventScrollOnSwipe: true,
      trackMouse: true,
    });
  
    return (
      <div
        {...swipeHandlers}
        className={`p-4 rounded-lg shadow border transition-all duration-300 ${
          isDeleting ? "bg-red-100" : "bg-white"
        }`}
      >
        <div className="flex justify-between items-start">
          <div className="w-full">
            <p className="font-semibold text-gray-800">{lead.name || "—"}</p>
            <p className="text-sm text-gray-600">{lead.email}</p>
            <p className="text-sm text-gray-600">{lead.phone}</p>
            <div className="flex gap-2 mt-2 flex-wrap">
              <span className={`px-2 py-1 text-xs rounded ${sourceBadge(lead.source)}`}>
                {lead.source || "—"}
              </span>
              <span className={`px-2 py-1 text-xs rounded ${statusBadge(lead.status)}`}>
                {lead.status || "nuevo"}
              </span>
              <span className="text-xs">Score: {lead.aiScore ?? "—"}</span>
            </div>

            {/* Resumen de lo que solicitó el cliente */}
            {Array.isArray(lead.cardInformation) && lead.cardInformation.length > 0 && (
              <div className="mt-3 bg-orange-50 border border-orange-100 rounded-lg px-3 py-2">
                <p className="text-xs font-semibold uppercase text-orange-600 mb-1">Productos solicitados</p>
                <ul className="space-y-1">
                  {lead.cardInformation.map((item, i) => (
                    <li key={i} className="text-xs text-gray-700 flex items-start gap-1">
                      <span className="text-orange-400 mt-0.5">•</span>
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
                {lead.meta?.projectLocation && (
                  <p className="text-xs text-gray-500 mt-1">📍 {lead.meta.projectLocation}</p>
                )}
                {lead.meta?.notes && (
                  <p className="text-xs text-gray-500 mt-1 italic">"{lead.meta.notes}"</p>
                )}
              </div>
            )}

            <div className="mt-4 space-y-1">
              <label className="text-xs font-semibold uppercase text-gray-500">
                Vendedor asignado
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                value={lead.assignedToUid || lead.assignedTo || ""}
                disabled={assigningLeadId === lead.id || loadingSellers}
                onChange={(e) => handleAssignSeller(lead.id, e.target.value)}
              >
                <option value="">Seleccionar vendedor</option>
                {sellers.map((seller) => (
                  <option key={seller.id} value={seller.uid || seller.id}>
                    {seller.name || seller.email}
                  </option>
                ))}
              </select>
              {isLeadAssigned(lead) && (
                <p className="text-xs text-gray-500">
                  {lead.assignedToName} · {lead.assignedToEmail}
                </p>
              )}
            </div>

            {/* ✅ Acciones agregadas para móvil */}
            <div className="flex flex-wrap gap-2 mt-4">
              {/* Calificar 80 */}
              <button
                onClick={async () => {
                  try {
                    await updateLead(lead.id, { status: "en_proceso", aiScore: 80 });
                    setNotification(`Lead ${lead.name} calificado (80)`);
                    fetchLeads();
                  } catch (e) {
                    console.error(e);
                  }
                }}
                className="text-xs px-3 py-1 rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
              >
                Calificar 80
              </button>
  
              {/* Convertir */}
              <button
                onClick={async () => {
                  try {
                    await convertLead(lead.id);
                    setNotification(`Lead ${lead.name} convertido a cliente`);
                    fetchLeads();
                  } catch (e) {
                    console.error(e);
                  }
                }}
                className="text-xs px-3 py-1 rounded bg-green-100 text-green-700 hover:bg-green-200"
              >
                Convertir
              </button>
  
              {/* Inactivar */}
              <button
                onClick={async () => {
                  try {
                    await updateLead(lead.id, { status: "inactivo" });
                    setNotification(`Lead ${lead.name} inactivado`);
                    fetchLeads();
                  } catch (e) {
                    console.error(e);
                  }
                }}
                className="text-xs px-3 py-1 rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
              >
                Inactivar
              </button>
  
              {/* Tus acciones originales */}
              <a
                href={`sms:${lead.phone}?body=Hola ${lead.name}, gracias por tu interés.`}
                className="bg-orange-100 text-orange-700 hover:bg-orange-200 p-2 rounded-full"
                title="Enviar SMS"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </a>
              <a
                href={`https://wa.me/${lead.phone}?text=Hola ${lead.name}, gracias por tu interés.`}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-green-100 text-green-700 hover:bg-green-200 p-2 rounded-full"
                title="Enviar WhatsApp"
              >
                <PaperAirplaneIcon className="h-5 w-5" />
              </a>
              <button
                onClick={() => openDetailModal(lead)}
                className="text-xs px-3 py-1 rounded bg-orange-100 text-orange-700 hover:bg-orange-200 font-semibold"
              >
                Ver Solicitud
              </button>
              <button
                onClick={() => openTimelineModal(lead)}
                className="text-xs px-3 py-1 rounded bg-purple-100 text-purple-700 hover:bg-purple-200"
              >
                Seguimiento
              </button>
              <button
                onClick={() => handleDelete(lead)}
                className="bg-red-100 text-red-700 hover:bg-red-200 p-2 rounded-full"
                title="Eliminar Lead"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  };
  

  return (
    <div className="p-6 max-w-7xl mx-auto bg-white min-h-screen">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-orange-600">Leads Capturados</h2>
        <button
          onClick={fetchLeads}
          className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-4 py-2 rounded shadow"
        >
          <ArrowPathIcon className="w-5 h-5" />
          Recargar
        </button>
      </div>

      <div className="mb-4 rounded-2xl border border-emerald-100 bg-emerald-50/70 p-4">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <p className="text-xs font-semibold uppercase tracking-wide text-emerald-700">
              Distribución por vendedor
            </p>
            <p className="text-sm text-emerald-900">Controla qué leads siguen en la bandeja general.</p>
          </div>
        </div>
        <div className="mt-3 flex flex-nowrap gap-2 overflow-x-auto pb-1">
          {[
            { id: "unassigned", label: `Sin asignar (${assignmentCounts.unassigned})` },
            { id: "assigned", label: `Asignados (${assignmentCounts.assigned})` },
            { id: "all", label: `Todos (${leads.length})` },
          ].map((tab) => {
            const isActive = assignmentFilter === tab.id;
            return (
              <button
                key={tab.id}
                onClick={() => setAssignmentFilter(tab.id)}
                className={[
                  "inline-flex flex-none items-center gap-2 whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition",
                  isActive
                    ? "border-emerald-700 bg-emerald-600 text-white shadow-lg shadow-emerald-100"
                    : "border-emerald-200 bg-white/80 text-emerald-800 hover:border-emerald-400",
                ].join(" ")}
              >
                <span
                  className={[
                    "inline-flex min-w-[1.75rem] items-center justify-center rounded-full text-xs font-bold",
                    isActive ? "bg-white/30 text-white" : "bg-emerald-100 text-emerald-700",
                  ].join(" ")}
                >
                  {tab.label.match(/\((\d+)\)/)?.[1] ?? ''}
                </span>
                <span>{tab.label.replace(/\s\(\d+\)/, '')}</span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="mb-6 rounded-2xl border border-orange-100 bg-orange-50/70 p-4">
        <p className="text-xs font-semibold uppercase tracking-wide text-orange-700">Estado del lead</p>
        <div className="mt-3 flex flex-nowrap gap-2 overflow-x-auto pb-1">
          {[
            { id: "activos", label: `Activos (${statusCounts.activos})` },
            { id: "convertidos", label: `Convertidos (${statusCounts.convertidos})` },
            { id: "inactivos", label: `Inactivos (${statusCounts.inactivos})` },
            { id: "todos", label: "Todos" },
          ].map((tab) => (
            <button
              key={tab.id}
              onClick={() => setStatusFilter(tab.id)}
              className={[
                "flex-none whitespace-nowrap rounded-full border px-4 py-2 text-sm font-semibold transition",
                statusFilter === tab.id
                  ? "border-orange-600 bg-orange-500 text-white shadow-lg shadow-orange-100"
                  : "border-orange-200 bg-white/80 text-orange-800 hover:border-orange-400",
              ].join(" ")}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Notificación */}
      {notification && (
        <div className="mb-4 bg-green-100 text-green-800 border border-green-300 px-4 py-2 rounded">
          {notification}
        </div>
      )}

      {/* 📱 Vista móvil */}
      <div className="block lg:hidden space-y-4">
        {filteredLeads.length === 0 ? (
          <p className="text-center py-6 text-gray-500">No hay leads disponibles</p>
        ) : (
          filteredLeads.map((lead) => <LeadCard key={lead.id} lead={lead} />)
        )}
      </div>

      {/* 💻 Vista escritorio */}
      <div className="hidden lg:block overflow-x-auto bg-gray-50 shadow rounded-lg">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-orange-100 text-orange-700">
            <tr>
              <th className="text-left px-4 py-2">Nombre</th>
              <th className="text-left px-4 py-2">Contacto</th>
              <th className="text-left px-4 py-2 min-w-[200px]">Solicitud</th>
              <th className="text-left px-4 py-2">Fuente</th>
              <th className="text-left px-4 py-2">Estado</th>
              <th className="text-left px-4 py-2">Score</th>
              <th className="text-left px-4 py-2">Vendedor</th>
              <th className="text-left px-4 py-2">Acciones</th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-100">
            {filteredLeads.length === 0 ? (
              <tr>
                <td colSpan="8" className="text-center py-6 text-gray-500">
                  No hay leads disponibles
                </td>
              </tr>
            ) : (
              filteredLeads.map((lead) => (
                <tr key={lead.id} className="hover:bg-orange-50/40 transition-colors">
                  <td className="px-4 py-3">
                    <p className="font-medium text-gray-800">{lead.name}</p>
                    {lead.meta?.projectLocation && (
                      <p className="text-xs text-gray-400 mt-0.5">📍 {lead.meta.projectLocation}</p>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <p className="text-sm text-gray-700">{lead.email}</p>
                    <p className="text-sm text-gray-500">{lead.phone}</p>
                  </td>
                  <td className="px-4 py-3">
                    {Array.isArray(lead.cardInformation) && lead.cardInformation.length > 0 ? (
                      <div className="space-y-1">
                        {lead.cardInformation.slice(0, 2).map((item, i) => (
                          <p key={i} className="text-xs text-gray-700 leading-snug">• {item}</p>
                        ))}
                        {lead.cardInformation.length > 2 && (
                          <p className="text-xs text-gray-400">+{lead.cardInformation.length - 2} más</p>
                        )}
                        <button
                          onClick={() => openDetailModal(lead)}
                          className="mt-1 text-xs text-orange-600 hover:text-orange-800 font-semibold underline underline-offset-2"
                        >
                          Ver solicitud completa →
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400">Sin detalle</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded ${sourceBadge(lead.source)}`}>
                      {lead.source || "—"}
                    </span>
                  </td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs rounded ${statusBadge(lead.status)}`}>
                      {lead.status || "nuevo"}
                    </span>
                  </td>
                  <td className="px-4 py-3">{lead.aiScore ?? "—"}</td>
                  <td className="px-4 py-3">
                    <div className="space-y-1">
                      <select
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm"
                        value={lead.assignedToUid || lead.assignedTo || ""}
                        disabled={assigningLeadId === lead.id || loadingSellers}
                        onChange={(e) => handleAssignSeller(lead.id, e.target.value)}
                      >
                        <option value="">Seleccionar vendedor</option>
                        {sellers.map((seller) => (
                          <option key={seller.id} value={seller.uid || seller.id}>
                            {seller.name || seller.email}
                          </option>
                        ))}
                      </select>
                      {isLeadAssigned(lead) ? (
                        <p className="text-xs text-gray-500">
                          {lead.assignedToName} · {lead.assignedToEmail}
                        </p>
                      ) : (
                        <p className="text-xs text-gray-400">Sin asignar</p>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* Calificar rápido */}
                      <button
                        onClick={async () => {
                          try {
                            await updateLead(lead.id, {
                              status: "en_proceso",
                              aiScore: 80,
                            });
                            setNotification(`Lead ${lead.name} calificado (80)`);
                            fetchLeads();
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                        className="px-3 py-1 text-xs rounded bg-blue-100 text-blue-700 hover:bg-blue-200"
                      >
                        Calificar 80
                      </button>

                      {/* Convertir a cliente */}
                      <button
                        onClick={async () => {
                          try {
                            await convertLead(lead.id);
                            setNotification(`Lead ${lead.name} convertido a cliente`);
                            fetchLeads();
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                        className="px-3 py-1 text-xs rounded bg-green-100 text-green-700 hover:bg-green-200"
                      >
                        Convertir
                      </button>

                      {/* Inactivar */}
                      <button
                        onClick={async () => {
                          try {
                            await updateLead(lead.id, { status: "inactivo" });
                            setNotification(`Lead ${lead.name} inactivado`);
                            fetchLeads();
                          } catch (e) {
                            console.error(e);
                          }
                        }}
                        className="px-3 py-1 text-xs rounded bg-gray-100 text-gray-700 hover:bg-gray-200"
                      >
                        Inactivar
                      </button>

                      {/* SMS / WhatsApp / Eliminar */}
                      <button
                        onClick={() => openDetailModal(lead)}
                        className="px-3 py-1 text-xs rounded bg-orange-100 text-orange-700 hover:bg-orange-200 font-semibold"
                      >
                        Ver Solicitud
                      </button>
                      <button
                        onClick={() => openTimelineModal(lead)}
                        className="px-3 py-1 text-xs rounded bg-purple-100 text-purple-700 hover:bg-purple-200"
                      >
                        Seguimiento
                      </button>
                      <a
                        href={`sms:${lead.phone}?body=Hola ${lead.name}, gracias por tu interés.`}
                        className="bg-orange-100 text-orange-700 hover:bg-orange-200 p-2 rounded-full"
                        title="SMS"
                      >
                        <PaperAirplaneIcon className="h-5 w-5" />
                      </a>
                      <a
                        href={`https://wa.me/${lead.phone}?text=Hola ${lead.name}, gracias por tu interés.`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="bg-green-100 text-green-700 hover:bg-green-200 p-2 rounded-full"
                        title="WhatsApp"
                      >
                        <PaperAirplaneIcon className="h-5 w-5" />
                      </a>
                      <button
                        onClick={() => handleDelete(lead)}
                        className="bg-red-100 text-red-700 hover:bg-red-200 p-2 rounded-full"
                        title="Eliminar"
                      >
                        <TrashIcon className="h-5 w-5" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de confirmación */}
      {leadToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-30 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-md w-full">
            <h3 className="text-lg font-semibold mb-4 text-gray-800">
              ¿Eliminar a {leadToDelete.name}?
            </h3>
            <p className="text-sm text-gray-600 mb-4">
              Esta acción no se puede deshacer.
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={cancelDelete}
                className="bg-gray-200 hover:bg-gray-300 text-gray-700 px-4 py-2 rounded"
              >
                Cancelar
              </button>
              <button
                onClick={confirmDelete}
                className="bg-red-500 hover:bg-red-600 text-white px-4 py-2 rounded"
              >
                Eliminar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de detalle / solicitud del cliente */}
      {detailLead && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center px-4">
          <div className="bg-white w-full max-w-2xl rounded-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="flex justify-between items-start px-6 py-5 border-b bg-orange-50 rounded-t-2xl">
              <div>
                <p className="text-xs uppercase tracking-widest text-orange-500 font-semibold mb-1">Solicitud de Cotización</p>
                <h3 className="text-xl font-bold text-gray-900">{detailLead.name || "—"}</h3>
                <div className="flex flex-wrap gap-3 mt-1 text-sm text-gray-600">
                  {detailLead.email && <span>✉️ {detailLead.email}</span>}
                  {detailLead.phone && <span>📞 {detailLead.phone}</span>}
                </div>
                {detailLead.createdAt && (
                  <p className="text-xs text-orange-400 mt-2">
                    📅 Recibida el{" "}
                    {detailLead.createdAt?.toDate
                      ? detailLead.createdAt.toDate().toLocaleString("es-PR", { dateStyle: "medium", timeStyle: "short" })
                      : new Date(detailLead.createdAt).toLocaleString("es-PR", { dateStyle: "medium", timeStyle: "short" })}
                  </p>
                )}
              </div>
              <button onClick={closeDetailModal} className="text-gray-400 hover:text-red-500 text-2xl leading-none mt-1">✕</button>
            </div>

            <div className="px-6 py-5 space-y-5">
              {/* Ubicación y tipo de proyecto */}
              {(detailLead.meta?.projectLocation || detailLead.meta?.projectType) && (
                <div className="flex flex-wrap gap-4 text-sm">
                  {detailLead.meta?.projectLocation && (
                    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                      <span className="text-lg">📍</span>
                      <div>
                        <p className="text-xs text-gray-400 uppercase font-semibold">Ubicación</p>
                        <p className="text-gray-800 font-medium">{detailLead.meta.projectLocation}</p>
                      </div>
                    </div>
                  )}
                  {detailLead.meta?.projectType && (
                    <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                      <span className="text-lg">🏠</span>
                      <div>
                        <p className="text-xs text-gray-400 uppercase font-semibold">Tipo</p>
                        <p className="text-gray-800 font-medium capitalize">{detailLead.meta.projectType}</p>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Productos / modelos solicitados */}
              {Array.isArray(detailLead.meta?.models) && detailLead.meta.models.length > 0 ? (
                <div>
                  <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-3">
                    Productos solicitados ({detailLead.meta.models.length})
                  </h4>
                  <div className="space-y-3">
                    {detailLead.meta.models.map((model, i) => (
                      <div key={i} className="border border-gray-200 rounded-xl p-4 bg-white shadow-sm">
                        <div className="flex items-start gap-3">
                          {model.image && (
                            <img src={model.image} alt={model.name || ""} className="w-16 h-16 rounded-lg object-cover flex-shrink-0 border" />
                          )}
                          <div className="flex-1 min-w-0">
                            <p className="font-semibold text-gray-800">{model.name || model.designId || "Modelo"}</p>
                            <p className="text-xs text-orange-600 font-medium">{model.categoryName || model.categoryId}</p>
                            {model.description && <p className="text-xs text-gray-500 mt-0.5">{model.description}</p>}
                            <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-gray-600">
                              {(model.height || model.width) && (
                                <span>📐 {model.height || "?"}" × {model.width || "?"}"</span>
                              )}
                              {model.quantity && <span>🔢 Cantidad: {model.quantity}</span>}
                              {model.series && (
                                <span>🪟 Serie: {model.series.replace("lama", "Lama ")}</span>
                              )}
                              {(() => {
                                const unitPrice = typeof model.estimate === "object"
                                  ? Number(model.estimate?.price) || 0
                                  : Number(model.estimate) || 0;
                                const qty = Number(model.quantity) || 1;
                                if (!unitPrice) return null;
                                return (
                                  <span className="font-semibold text-green-700">
                                    💰 ${unitPrice.toLocaleString("en-US", { minimumFractionDigits: 2 })} c/u
                                    {qty > 1 && ` → $${(unitPrice * qty).toLocaleString("en-US", { minimumFractionDigits: 2 })} total`}
                                  </span>
                                );
                              })()}
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              ) : Array.isArray(detailLead.cardInformation) && detailLead.cardInformation.length > 0 ? (
                <div>
                  <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">Productos solicitados</h4>
                  <ul className="space-y-1">
                    {detailLead.cardInformation.map((item, i) => (
                      <li key={i} className="text-sm text-gray-700 flex items-start gap-2">
                        <span className="text-orange-400 mt-0.5">•</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : null}

              {/* Acabados */}
              {detailLead.meta?.finishes && (
                <div>
                  <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">Acabados seleccionados</h4>
                  <div className="flex flex-wrap gap-2">
                    {detailLead.meta.finishes.color && (
                      <span className="bg-gray-100 text-gray-700 text-xs px-3 py-1 rounded-full">
                        🎨 Color: <b>{detailLead.meta.finishes.color}</b>
                      </span>
                    )}
                    {detailLead.meta.finishes.glass && (
                      <span className="bg-blue-50 text-blue-700 text-xs px-3 py-1 rounded-full">
                        🔷 Vidrio: <b>{detailLead.meta.finishes.glass}</b>
                      </span>
                    )}
                    {detailLead.meta.finishes.finish && (
                      <span className="bg-purple-50 text-purple-700 text-xs px-3 py-1 rounded-full">
                        ✨ Acabado: <b>{detailLead.meta.finishes.finish}</b>
                      </span>
                    )}
                  </div>
                </div>
              )}

              {/* Certificaciones */}
              {Array.isArray(detailLead.meta?.certifications) && detailLead.meta.certifications.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">Certificaciones requeridas</h4>
                  <div className="flex flex-wrap gap-2">
                    {detailLead.meta.certifications.map((cert, i) => (
                      <span key={i} className="bg-green-50 text-green-700 text-xs px-3 py-1 rounded-full border border-green-200">
                        ✅ {cert}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Accesorios */}
              {Array.isArray(detailLead.meta?.accessories) && detailLead.meta.accessories.length > 0 && (
                <div>
                  <h4 className="text-sm font-bold text-gray-700 uppercase tracking-wide mb-2">Accesorios</h4>
                  <div className="space-y-1">
                    {detailLead.meta.accessories.map((acc, i) => (
                      <div key={i} className="flex justify-between text-sm text-gray-700 border-b border-gray-100 pb-1">
                        <span>{acc.name} × {acc.quantity} {acc.unit || ""}</span>
                        {acc.subtotal != null && (
                          <span className="font-medium">${Number(acc.subtotal).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                        )}
                      </div>
                    ))}
                    {detailLead.meta.accessoriesTotal > 0 && (
                      <div className="flex justify-between text-sm font-bold text-gray-800 pt-1">
                        <span>Total accesorios</span>
                        <span>${Number(detailLead.meta.accessoriesTotal).toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Notas del cliente */}
              {(detailLead.meta?.notes || detailLead.additionalInfo) && (
                <div className="bg-yellow-50 border border-yellow-200 rounded-xl px-4 py-3">
                  <h4 className="text-xs font-bold text-yellow-700 uppercase tracking-wide mb-1">Notas del cliente</h4>
                  <p className="text-sm text-gray-700 italic">
                    "{detailLead.meta?.notes || detailLead.additionalInfo}"
                  </p>
                </div>
              )}

              {/* Estimado total real */}
              {(() => {
                const models = detailLead.meta?.models || [];
                const modelsTotal = models.reduce((sum, m) => {
                  const unitPrice = typeof m.estimate === "object"
                    ? Number(m.estimate?.price) || 0
                    : Number(m.estimate) || 0;
                  const qty = Number(m.quantity) || 1;
                  return sum + unitPrice * qty;
                }, 0);
                const accessoriesTotal = Number(detailLead.meta?.accessoriesTotal) || 0;
                const grandTotal = modelsTotal + accessoriesTotal;
                if (grandTotal <= 0) return null;
                return (
                  <div className="bg-green-50 border border-green-200 rounded-xl px-4 py-3">
                    <p className="text-xs font-bold text-green-700 uppercase tracking-wide mb-2">Estimado preliminar total</p>
                    <div className="space-y-1 text-sm text-gray-700">
                      {modelsTotal > 0 && (
                        <div className="flex justify-between">
                          <span>Productos ({models.length} línea{models.length !== 1 ? "s" : ""})</span>
                          <span>${modelsTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      {accessoriesTotal > 0 && (
                        <div className="flex justify-between">
                          <span>Accesorios</span>
                          <span>${accessoriesTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                        </div>
                      )}
                      <div className="flex justify-between font-bold text-green-800 text-base border-t border-green-200 pt-2 mt-1">
                        <span>Total estimado</span>
                        <span>${grandTotal.toLocaleString("en-US", { minimumFractionDigits: 2 })}</span>
                      </div>
                    </div>
                    <p className="text-xs text-green-500 mt-1">Sujeto a medición en sitio</p>
                  </div>
                );
              })()}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 rounded-b-2xl flex justify-between items-center gap-3 flex-wrap">
              <button
                onClick={() => { closeDetailModal(); openTimelineModal(detailLead); }}
                className="px-4 py-2 rounded-lg bg-purple-600 hover:bg-purple-700 text-white text-sm font-semibold"
              >
                Ir a Seguimiento
              </button>
              <button
                onClick={closeDetailModal}
                className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300 text-gray-700 text-sm font-semibold"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de seguimiento */}
      {timelineLead && (
        <div className="fixed inset-0 bg-black bg-opacity-40 z-50 flex items-center justify-center px-4">
          <div className="bg-white w-full max-w-3xl rounded-lg shadow-lg max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center border-b px-6 py-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-800">
                  Seguimiento de {timelineLead.name || timelineLead.email}
                </h3>
                <p className="text-xs text-gray-500">
                  Estado actual: {timelineLead.status || "nuevo"} • Score:{" "}
                  {timelineLead.aiScore ?? "—"}
                </p>
              </div>
              <button
                onClick={closeTimelineModal}
                className="text-gray-500 hover:text-red-600"
              >
                ✕
              </button>
            </div>

            {campaignTemplates.length > 0 && (
              <div className="px-6 py-3 bg-amber-50 border-b border-amber-100 space-y-2">
                <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">
                  Campañas sugeridas por IA
                </p>
                <div className="flex flex-wrap gap-2">
                  {campaignTemplates.map((template) => (
                    <button
                      key={template.id}
                      disabled={sendingCampaign}
                      onClick={() => handleSendCampaign(template)}
                      className="px-3 py-1 text-xs rounded-full bg-amber-200 text-amber-800 hover:bg-amber-300 disabled:opacity-50"
                    >
                      {sendingCampaign ? "Enviando..." : template.label}
                    </button>
                  ))}
                </div>
              </div>
            )}

            <div className="grid md:grid-cols-2 gap-0">
              <div className="border-r px-6 py-4 space-y-4">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                  Historial de notas
                </h4>
                {timelineLoading ? (
                  <p className="text-sm text-gray-500">Cargando…</p>
                ) : timelineEntries.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    Aún no hay notas registradas.
                  </p>
                ) : (
                  <ul className="space-y-3">
                    {timelineEntries.map((entry) => (
                      <li
                        key={entry.id}
                        className="border rounded-lg p-3 text-sm space-y-1"
                      >
                        <p className="text-gray-800">{entry.summary}</p>
                        {entry.nextStep && (
                          <p className="text-xs text-blue-600">
                            Próximo paso: {entry.nextStep}
                          </p>
                        )}
                        {entry.remindAt && (
                          <p className="text-xs text-gray-500">
                            Recordar el{" "}
                            {entry.remindAt.toDate
                              ? entry.remindAt
                                  .toDate()
                                  .toLocaleString("es-PR")
                              : new Date(entry.remindAt).toLocaleString(
                                  "es-PR"
                                )}
                          </p>
                        )}
                        <p className="text-xs text-gray-400 flex justify-between">
                          <span>Canal: {entry.channel}</span>
                          <span>
                            {entry.createdAt?.toDate
                              ? entry.createdAt
                                  .toDate()
                                  .toLocaleString("es-PR")
                              : "—"}
                          </span>
                        </p>
                      </li>
                    ))}
                  </ul>
                )}
              </div>

              <div className="px-6 py-4 bg-gray-50">
                <h4 className="text-sm font-semibold text-gray-700 uppercase tracking-wide mb-4">
                  Nueva nota / Automatización
                </h4>
                <form className="space-y-4" onSubmit={handleTimelineSubmit}>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">
                      Resumen del contacto
                    </label>
                    <textarea
                      className="w-full border rounded p-2 text-sm"
                      rows={3}
                      value={noteForm.summary}
                      onChange={(e) =>
                        setNoteForm({ ...noteForm, summary: e.target.value })
                      }
                      placeholder="Ej: Llamada completada, interesado en BookMe."
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600">
                      Próximo paso
                    </label>
                    <input
                      className="w-full border rounded p-2 text-sm"
                      value={noteForm.nextStep}
                      onChange={(e) =>
                        setNoteForm({ ...noteForm, nextStep: e.target.value })
                      }
                      placeholder="Ej: enviar demo, agendar llamada, etc."
                    />
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="block text-xs font-medium text-gray-600">
                        Canal sugerido
                      </label>
                      <select
                        className="w-full border rounded p-2 text-sm"
                        value={noteForm.channel}
                        onChange={(e) =>
                          setNoteForm({ ...noteForm, channel: e.target.value })
                        }
                      >
                        <option value="whatsapp">WhatsApp</option>
                        <option value="sms">SMS</option>
                        <option value="email">Email</option>
                        <option value="llamada">Llamada</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-gray-600">
                        Programar recordatorio
                      </label>
                      <input
                        type="datetime-local"
                        className="w-full border rounded p-2 text-sm"
                        value={noteForm.schedule}
                        onChange={(e) =>
                          setNoteForm({ ...noteForm, schedule: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <button
                    type="submit"
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white py-2 rounded text-sm font-medium"
                  >
                    Guardar seguimiento
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LeadsList;
